import { FormKitNode, FormKitPlugin } from '@formkit/core'
import { Ref, watchEffect } from 'vue'

/**
 * Automatically syncs the FormKit locale with the passed locale ref.
 *
 * Usage in formkit.config.ts:
 *
 *   plugins: [
 *     createResponsiveI18nPlugin(locale),
 *   ],
 *
 * If you are using vue-i18n, you can pass i18n.global.locale like so:
 *
 *   const i18n = createI18n({
 *     ...
 *     // This is necessary for i18n.global.locale to be typed as a ref
 *     // (expected by createFormKitConfig)
 *     legacy: false,
 *   })
 *
 *   createResponsiveI18nPlugin(i18n.global.locale)
 *
 * Because of the way FormKit plugins work, this plugin has to be registered on
 * every FormKit node with watchEffect. This seems inefficient, but it is the
 * only way to ensure that both new and existing FormKit nodes are updated when
 * the locale changes. It seems there is no other way outside of a Vue component
 * to modify the root config.
 */
export default function createResponsiveI18nPlugin (locale: Ref<string>): FormKitPlugin {
  return (node: FormKitNode) => {
    watchEffect(() => {
      if (!node.config.rootConfig) {
        // Not sure when the root config would ever be undefined, but we need
        // this for TypeScript to be happy
        return
      }
      if (node.config.rootConfig.locale === locale.value) {
        return
      }
      node.config.rootConfig.locale = locale.value
    })
  }
}
